import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ENV } from "@app/env";
import { AngularFireAuth } from "angularfire2/auth";
import { AngularFireDatabase } from "angularfire2/database";
import * as firebase from "firebase/app";
import { Observable } from "rxjs/Observable";
import { map, switchMap, retry, catchError } from "rxjs/operators";
import { IBackendProcess } from "shared/models/backend-process.model";
import { IBackendResponse } from "../shared/models/backend.model";
import { IAdministratorUser, IUser, IUserStats } from "../shared/models/user.model";
import { CategoriesData } from "./categories";
import { EventData } from "./event";
import { HistoryData } from "./history";
import { _TRACKING_ENTITIES, TrackingProvider } from "./tracking";
import { Utilities } from "./utilities";

@Injectable()
export class UserData {

  get defaultTemplate() {
    // tslint:disable-next-line:max-line-length
    return "https://firebasestorage.googleapis.com/v0/b/rebus-v4.appspot.com/o/EmailTemplates%2Frefunde_rebus.html?alt=media&token=2db0e471-7569-4943-9c77-8e5f05e0c6de";
  }

  private action: string = "update";

  constructor(
    private afDb: AngularFireDatabase,
    private afAuth: AngularFireAuth,
    private historyData: HistoryData,
    private trackingData: TrackingProvider,
    private http: HttpClient,
    private eventData: EventData,
    private UtilitiesData: Utilities,
    private categoriesData: CategoriesData,
    private utilities: Utilities
  ) { }

  public getLogin(equalTo): any {
    return firebase.database()
      .ref("/AdministratorList")
      .orderByChild("email")
      .equalTo(equalTo)
      .limitToFirst(1);
  }
  public resetUser(email: string, password: string) {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json");
    return this.http.post(`${ENV.backend}/users/resetUser`, {

      email,
      password,
    }, {
        headers,
      });
  }
  /**
   * For backendProcess MassiveUsers
   * @param client client
   * @param process proceso de carga masiva
   */
  public createProcess(client: string, process: any) {
    process.id = this.UtilitiesData.generateID();
    return new Promise((resolve, reject) => {
      return this.afDb.database.ref(`BackendProcess/${client}/${process.id}`)
        .set(process)
        .then(() => {
          resolve(process);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  public getUsersMassiveLoad(client: string, processId: string): Observable<IBackendProcess> {
    return this.afDb.object<IBackendProcess>(`BackendProcess/${client}/${processId}`).valueChanges();
  }
  public getUserAdminByUid(uid): any {
    return this.afDb.object(`AdministratorList/${uid}`).valueChanges();
  }

  public getUsersByClient(client: string): Observable<IUser[]> {
    return this.afDb.list<IUser>(`Clients/${client}/Users`).valueChanges();
  }

  public getUsersRegisterByClient(client: string): Observable<IUser[]> {
    return this.afDb.list<any>(`Clients/heroes/UserRegisters`).valueChanges();
  }

  public getUsersRegisterRef(clientId: string) {
    return this.afDb.list<any>(`/Clients/heroes/UserRegisters`).query.ref;
  }

  public getUsersRegister(clientId: string): Observable<any[]> {
    return this.afDb.list<any>(`/Clients/${clientId}/UserRegisters`).valueChanges();
  }

  public addUserFromRegister(clientId: string, user: any, uid: string) {
    const data = { ...user, id: uid, dni: user.id };
    return this.afDb.object(`/Clients/${clientId}/Users/${uid}`).update({
      ...data,
      created_at: firebase.database.ServerValue.TIMESTAMP,
    });
  }

  public updateStateUserRegisterApproved(clientId, user) {
    return this.afDb.object(`/Clients/${clientId}/UserRegisters/${user.id}`)
      .update({
        state: "approved",
      });
  }

  public updateStateUserRegisterRejected(clientId, user) {
    return this.afDb.object(`/Clients/${clientId}/UserRegisters/${user.id}/state`).set("rejected");
  }

  public getUserQuantityAll(clientId) {
    return this.afDb.list<any>(`/Clients/${clientId}/UserRegisters`).valueChanges();
  }

  public getUserQuantity(state, clientId): Observable<any[]> {
    return this.afDb.list<any>(`/Clients/${clientId}/UserRegisters`,
      (ref) => ref.orderByChild("state").equalTo(state))
      .valueChanges();
  }

  /**
   * Return the user Stats
   * @param clientId
   * @param userId
   * @author Sebastián Guevara
   */
  public getUserStatsPointsByUserId(clientId: string, userId: string): Observable<IUserStats> {
    return this.afDb.object<IUserStats>(`Clients/${clientId}/UserStats/${userId}`)
      .valueChanges();
  }

  public getUserStatsPointsSnapByUserId(clientId: string, userId: string) {
    return this.afDb.object<IUserStats>(`Clients/${clientId}/UserStats/${userId}`)
      .query.ref;
  }

  /**
   * Return the user events
   * @param clientId
   * @param userId
   * @author Sebastián Guevara
   */
  public getUserEventsByUserId(clientId: string, userId: string): Observable<any> {
    return this.afDb.object(`Clients/${clientId}/UserEvents/${userId}`)
      .valueChanges()
      .pipe(
        switchMap((usersEvents: any) => {
          const obs = [];
          if (usersEvents) {
            Object.keys(usersEvents).forEach((key) => {
              obs.push(this.eventData.getEventInfo(clientId, key));
            });
          }
          return usersEvents === null ? Observable.of([]) :
            Observable.combineLatest(...obs)
              .pipe(
                map((data) => {
                  this.UtilitiesData.consoleDecorator("User Events", data);
                  return data;
                }),
              );
        }),
      );
  }

  /**
   * Get the user categories
   * @param clientId
   * @param userId
   */
  public getUserCategoriesByUserId(clientId: string, userId: string): Observable<any> {
    return this.afDb.object(`Clients/${clientId}/Users/${userId}/interest`).valueChanges();
    // .pipe(switchMap((data) => {
    //   let obs = [];
    //   if (data) {
    //     Object.keys(data).forEach((key) => {
    //       obs.push(this.categoriesData.getClientCategoriesById(clientId, key));
    //     })
    //   }
    //   return (data === null) ? Observable.of([]) : Observable.combineLatest(obs)
    //     .pipe(map((data) => {
    //       this.UtilitiesData.consoleDecorator('User Interest ', data);
    //       return data;
    //     }))
    // }))
  }

  public setUserCategory(clientId: string, userId: string, categoryId: string, state: boolean) {
    return Observable.fromPromise(
      this.afDb.object(`Clients/${clientId}/Users/${userId}/interest/${categoryId}`).set((state) ? state : null));
  }

  public getUserByClient(client: string, userId: string) {
    return firebase.database()
      .ref(`Clients/${client}/Users`)
      .orderByChild("id")
      .equalTo(userId)
      .limitToFirst(1);
  }

  public updateUserInformationByClient(client: string, id: string, userInformation: IUser): Observable<any> {
    const updateUser = new Promise((resolve, reject) => {
      userInformation.update_at = new Date().getTime();
      this.afDb.object<IUser>(`Clients/${client}/Users/${userInformation.id}`)
        .update(userInformation).then(() => {
          this.historyData.set(
            `User Application Updated`,
            "users",
            "Update",
            userInformation,
          );
          this.trackingData.trackEntity(_TRACKING_ENTITIES.USERS, userInformation.id, client, userInformation);
          resolve();
        }).catch((error) => {
          console.error("Something big happen", error);
          reject(error);
        });
    });
    return Observable.fromPromise(updateUser);

  }

  public deleteUserByClient(client: string, id: string): Observable<any> {
    return Observable.fromPromise(
      this.afDb.object<IUser>(`Clients/${client}/Users/${id}`).remove());
  }

  public getAdministratorUsers(): Observable<IAdministratorUser[]> {
    return this.afDb.list<IAdministratorUser>(`AdministratorList`).valueChanges();
  }

  public getAdministratorUser(userId: string): Observable<IAdministratorUser> {
    return this.afDb.object<IAdministratorUser>(`AdministratorList/${userId}`).valueChanges();
  }

  public deleteAdministratorFromDB(user: IAdministratorUser): Observable<any> {
    const doIt = new Promise((resolve, reject) => {
      this.afDb.object<IUser>(`AdministratorList/${user.id}`).remove()
        .then(() => {
          this.historyData.set(
            `User Administrator Deleted`,
            "usersAdmin",
            "Delete",
            user,
          );
          resolve();
        }).catch((error) => {
          console.error("Something big happen", error);
          reject(error);
        });
    });
    return Observable.fromPromise(doIt);
  }

  public createUser(user: any) {
    // console.log("user to create on backend", user);
    const payload = {
      displayName: user.name,
      email: user.email,
      password: user.password,
    };
    return this.http.post(`${ENV.backend}/users/createUser`, payload)
      .catch((err) => {
        this.UtilitiesData.consoleDecorator("Error: ", err);
        return "Error creando usuario";
      })
      .map((res: any) => res.result.uid)
      .toPromise();
  }

  public createAdministratorUser(user: IAdministratorUser): Observable<any> {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json");

    return this.http.post(`${ENV.backend}/users/createUser`,
      {
        displayName: user.email,
        email: user.email,
        emailVerified: false,
        password: user.password,
        phoneNumber: "",
        photoURL: "",
      }
      , {
        headers,
      }).pipe(switchMap((userCreatedInfo: IBackendResponse) => {
        this.historyData.set(
          `User Administrator Created`,
          "usersAdmin",
          "Create",
          user,
        );
        return this.createUserAdministratorInformation(userCreatedInfo.result.uid, user);
      }));
  }

  public deleteAdministratorUser(userId: string, user): Observable<any> {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json");

    return this.http.post(`${ENV.backend}/users/deleteUser`,
      {
        usuarioId: userId,
      }
      , {
        headers,
      }).pipe(switchMap((userCreatedInfo: IBackendResponse) => {
        this.historyData.set(
          `User Administrator Deleted`,
          "usersAdmin",
          "Delete",
          user,
        );
        return this.deleteAdministratorFromDB(user);
      }));
  }

  public createUserAdministratorInformation(userId: string, user: IAdministratorUser): Observable<any> {
    return Observable.fromPromise(this.afDb.object(`AdministratorList/${userId}`).set({
      email: user.email,
      id: userId,
      managerClient: user.managerClient,
      profile: user.profile,
    }));
  }

  public createUsersFromObject(
    users: any[],
    client: string,
    password: string = null,
    passwordCustom: string = null,
    networkingState: boolean = false,
  ) {
    return this.http.post(
      `${ENV.backend}/users/createMultipleUsers`,
      { client, users, password, passwordCustom, networkingState },
    );
  }

  public updateUserAdministratorInformation(user: IAdministratorUser): Observable<any> {
    const updateUser = new Promise((resolve, reject) => {
      delete user.password;
      delete user.confirmPassword;
      this.afDb.object<IAdministratorUser>(`AdministratorList/${user.id}`).set(user).then(() => {
        this.historyData.set(
          `User Administrator Updated`,
          "usersAdmin",
          "update",
          user,
        );
        resolve();
      }).catch((error) => reject(error));
    });
    return Observable.fromPromise(updateUser);
  }

  public getJSONUsers(clientId: string, eventId: string = null) {
    let httpObs;
    if (eventId) {
      httpObs = this.http.get(`${ENV.backend}/users/${clientId}/${eventId}`);
    } else {
      httpObs = this.http.get(`${ENV.backend}/users/${clientId}`);
    }

    return httpObs;
  }

  public getAccessUsers(clientId: string) {
    return this.http.get(`${ENV.backend}/reports/accessuser/${clientId}`)
      .pipe(
        retry(3),
        catchError((err) => [this.utilities.consoleDecorator("Error report generate: ", err, "orange")]),
      );
  }

  /**
   * Carga masiva de usuarios
   *
   * @param clientId
   * @param processId
   * @param url
   * @param updateUser
   * @param updatePass
   * @param isNetworking
   * @param terms
   * @param interest
   * @param genericPass
   */
  public sendProcessBackEnd(
    clientId: string,
    processId: string,
    url: string,
    updateUser: boolean,
    updatePass: boolean,
    isNetworking: boolean,
    terms: boolean,
    interest: any,
    genericPass: string = null,
  ) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    return new Promise((resolve, reject) => {
      this.http
        .post(`${ENV.backend}/users/massive/${clientId}/${processId}`, {
          genericPass,
          interest,
          isNetworking,
          terms,
          updatePass,
          updateUser,
          url,
        }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public addPaymentUser(url, client) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return new Promise((resolve, reject) => {
      this.http.post(`${ENV.backend}/clubs/`, {
        client: client,
        "override": false,
        url,
      }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public addCouponsUser(url, client) {
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return new Promise((resolve, reject) => {
      this.http.post(`${ENV.backend}/coupons/massive/${client.id}/2DGFCP`, {
        client: client.id,
        url,
      }, { headers })
        .subscribe((data: any) => {
          resolve(data);
        }, (error) => { reject(error); });
    });
  }

  public sendNotificationUser(user, template = this.defaultTemplate) {
    const headers = new HttpHeaders()
      .set("Content-Type", "application/json");
    return this.http.post(`${ENV.backend}/notification/mail`,
      {
        from: "info@innpulsacolombia.com",
        params: {
          contraseña: user.password,
          nombre_usuario: user.name,
          usuario: ENV.mode === "Development" ? "soporte@rebus.com.co" : user.email,
        },
        subjet: ENV.mode === "Development" ? "PRUEBA NOTIFICACION" : "Registro Heroesfest.co",
        template,
        to: [user.email],
      }
      , {
        headers,
      });
  }

}
 // end file



// WEBPACK FOOTER //
// ./src/providers/user.ts